/** Espelho dos contratos da API (src/auth e src/nfe no backend). */

export type Papel = 'USER' | 'ADMIN';
export type TipoPessoa = 'FISICA' | 'JURIDICA';

export interface Usuario {
  id: string;
  /** Nome da pessoa (FISICA) ou razão social (JURIDICA). */
  nome: string;
  email: string;
  role: Papel;
  ativo: boolean;
  createdAt: string;

  tipoPessoa: TipoPessoa;
  /** Só dígitos: 11 (CPF) ou 14 (CNPJ). A formatação é feita na tela. */
  documento: string;
  telefone: string | null;

  cep: string;
  logradouro: string;
  numero: string;
  complemento: string | null;
  bairro: string;
  cidade: string;
  uf: string;

  /** Nulo = e-mail ainda não confirmado; ações que gastam recurso ficam presas. */
  emailVerificadoEm: string | null;

  /**
   * `true` = a senha foi definida por outra pessoa (bootstrap do admin, reset
   * pelo suporte). A API recusa tudo que não seja a troca; o painel redireciona.
   */
  senhaProvisoria: boolean;
  ultimoAcessoEm: string | null;
}

/** Corpo de POST /auth/registrar. */
export interface DadosCadastro {
  tipoPessoa: TipoPessoa;
  documento: string;
  nome: string;
  email: string;
  senha: string;
  telefone?: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento?: string;
  bairro: string;
  cidade: string;
  uf: string;
  /** Canal de aquisição — só com consentimento (ver util/visita.ts). */
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
}

/** GET /cadastro/cep/:cep */
export interface EnderecoCep {
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}

/** GET /cadastro/cnpj/:cnpj */
export interface DadosCnpj {
  documento: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  situacao: string | null;
  endereco: EnderecoCep & { numero: string; complemento: string | null };
}

/**
 * Resposta das rotas de sessão. Sem `accessToken`/`refreshToken` de propósito:
 * eles só existem em cookie `httpOnly`, fora do alcance do JavaScript.
 */
export interface RespostaSessao {
  usuario: Usuario;
  csrfToken: string;
  expiraEm: number;
}

export type StatusJob = 'PENDENTE' | 'PROCESSANDO' | 'CONCLUIDO' | 'ERRO';

export interface Job {
  id: string;
  status: StatusJob;
  total: number;
  /** XMLs obtidos. */
  ok: number;
  /** Chaves que a fonte não conhece — não são retentadas nem cobradas. */
  notfound: number;
  /** Falha transitória após esgotar as retentativas. */
  erro: number;
  createdAt: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  ownerId?: string;
}

/** `GET /nfe/lote/:jobId` — o job com os derivados de progresso. */
export interface JobDetalhado extends Job {
  processados: number;
  pendentes: number;
  /** 0 a 1. */
  progresso: number;
}

/** `GET /nfe/lote/resumo` — números da visão geral. */
export interface Resumo {
  xmlsBaixados: number;
  pendentes: number;
  naoEncontradas: number;
  comErro: number;
  jobsEmAndamento: number;
  totalJobs: number;
}

export interface ApiKey {
  id: string;
  nome: string;
  ultimoUso: string | null;
  expiraEm: string | null;
  revogadaEm: string | null;
  createdAt: string;
}

/** Só na criação: `token` é o segredo, exibido uma única vez. */
export interface ApiKeyCriada {
  chave: ApiKey;
  token: string;
}

export interface JobCriado {
  jobId: string;
  total: number;
  status: 'PENDENTE';
}

/* ── SPED ────────────────────────────────────────────────────────────────── */

export type StatusAnalise = 'PROCESSANDO' | 'CONCLUIDA' | 'ERRO';

export interface ArquivoSped {
  id: string;
  nomeArquivo: string;
  /** EFD_ICMS_IPI, EFD_CONTRIBUICOES, ECD, ECF, DESCONHECIDO. */
  tipo: string;
  cnpj: string | null;
  nomeEmpresa: string | null;
  competenciaInicio: string | null;
  competenciaFim: string | null;
  notas: number;
  /** Por que o arquivo não rendeu notas (ECD, formato errado, mês sem doc). */
  aviso: string | null;
}

export interface AnaliseSped {
  id: string;
  nome: string;
  status: StatusAnalise;
  arquivos: number;
  totalNotas: number;
  erro: string | null;
  createdAt: string;
  finishedAt: string | null;
  arquivosDetalhe: ArquivoSped[];
}

/** Contagens dos filtros, já considerando a seleção atual. */
export interface Facetas {
  total: number;
  competencias: Array<{
    ano: number;
    notas: number;
    meses: Array<{ mes: number; notas: number }>;
  }>;
  cfops: Array<{ cfop: string; notas: number }>;
  operacoes: Array<{ operacao: string; notas: number }>;
  emissoes: Array<{ emissao: string; notas: number }>;
  modelos: Array<{ modelo: string; notas: number }>;
  situacoes: Array<{ situacao: string; notas: number }>;
}

export interface NotaSped {
  id: string;
  chave: string;
  modelo: string;
  situacao: string;
  operacao: 'E' | 'S';
  emissao: 'P' | 'T';
  dataDoc: string;
  ano: number;
  mes: number;
  cfops: string[];
  participante: string | null;
  participanteDoc: string | null;
  valor: string | null;
}

export interface PaginaNotas {
  total: number;
  pagina: number;
  porPagina: number;
  itens: NotaSped[];
}

/** Seleção de filtros da tela. Campo vazio = sem restrição. */
export interface FiltroSped {
  /** `2026` (ano inteiro) ou `2026-03` (mês). */
  competencias: string[];
  cfops: string[];
  operacoes: string[];
  emissoes: string[];
  modelos: string[];
  situacoes: string[];
  busca: string;
}

export const FILTRO_VAZIO: FiltroSped = {
  competencias: [],
  cfops: [],
  operacoes: [],
  emissoes: [],
  modelos: [],
  situacoes: [],
  busca: '',
};

/* ── Créditos ────────────────────────────────────────────────────────────── */

/** Tabela pública: faixas de preço e o bônus vigente (0 = promoção desligada). */
export interface Precos {
  faixas: FaixaPreco[];
  bonusCadastroCentavos: number;
}

export interface FaixaPreco {
  de: number;
  /** `null` na última faixa (sem teto). */
  ate: number | null;
  precoCentavos: number;
}

export interface ReservaCredito {
  id: string;
  jobId: string;
  chavesReservadas: number;
  precoUnitarioCentavos: number;
  valorReservadoCentavos: number;
  createdAt: string;
}

export interface ResumoCreditos {
  /** Disponível para novas reservas. */
  saldoCentavos: number;
  /** Travado em lotes em andamento. */
  reservadoCentavos: number;
  totalCentavos: number;
  reservas: ReservaCredito[];
  tabelaPrecos: FaixaPreco[];
}

export type TipoLancamento =
  'RECARGA' | 'RESERVA' | 'CONSUMO' | 'ESTORNO' | 'AJUSTE' | 'BONUS';

export interface Lancamento {
  id: string;
  tipo: TipoLancamento;
  /** SEMPRE positivo — o `tipo` define o sentido. */
  valorCentavos: number;
  saldoDepoisCentavos: number;
  jobId: string | null;
  descricao: string;
  createdAt: string;
}

export interface Orcamento {
  chaves: number;
  /** XMLs entregues à conta nos últimos 30 dias — entra na faixa de preço. */
  volumeRecente: number;
  precoUnitarioCentavos: number;
  totalCentavos: number;
  saldoCentavos: number;
  suficiente: boolean;
  faltamCentavos: number;
}

export type StatusRecarga = 'PENDENTE' | 'PAGA' | 'EXPIRADA' | 'CANCELADA' | 'ESTORNADA';

export interface Recarga {
  id: string;
  valorCentavos: number;
  status: StatusRecarga;
  asaasPaymentId: string | null;
  /** Copia e cola do Pix. */
  pixPayload: string | null;
  /** PNG do QR Code em base64, sem o prefixo `data:`. */
  pixQrCode: string | null;
  expiraEm: string | null;
  pagaEm: string | null;
  createdAt: string;
}

export interface LimitesRecarga {
  minimoCentavos: number;
  maximoCentavos: number;
  /** `false` quando a integração de pagamento não está configurada. */
  disponivel: boolean;
  /** Bônus por recarga em vigor, ou `null` sem promoção. */
  promocao: PromocaoRecarga | null;
}

export interface PromocaoRecarga {
  /** A partir de `minimoCentavos`, `percentual`% de bônus. Ordenadas. */
  faixas: Array<{ minimoCentavos: number; percentual: number }>;
  /** Último dia valendo, `AAAA-MM-DD`. */
  ate: string;
}

/* ── Painel administrativo ───────────────────────────────────────────────── */

export interface ResumoAdmin {
  usuarios: {
    total: number;
    verificados: number;
    naoVerificados: number;
    inativos: number;
    novosNoMes: number;
    /** Movimentaram crédito nos últimos 7 dias. */
    ativosSemana: number;
  };
  creditos: {
    /** Passivo: dinheiro já recebido que ainda vira serviço. */
    saldoEmContas: number;
    reservado: number;
    reservasAtivas: number;
    recebidoTotal: number;
    recargasPagas: number;
    consumoTotal: number;
    consumoMes: number;
  };
  operacao: {
    analisesSped: number;
    lotes: number;
    lotesProcessando: number;
    lotesPendentes: number;
    /** Finalizados ainda dentro da retenção (o ZIP segue disponível). */
    lotesConcluidos: number;
    lotesErro: number;
    /** Soma só dos lotes PENDENTE/PROCESSANDO. */
    chavesEmAndamento: { processadas: number; total: number };
    /** Downloads em voo agora, somando todos os lotes. */
    downloadsAtivos: number;
    limites: {
      jobsSimultaneos: number;
      downloadsSimultaneosGlobais: number;
      workersPorJob: number;
    };
  };
}

export interface EstatisticaCobranca {
  quantidade: number;
  bruto: number;
  /** Bruto menos a taxa do provedor. */
  liquido: number;
}

export type FinanceiroAdmin =
  | { disponivel: false; motivo: string }
  | {
      disponivel: true;
      saldoAsaas: number;
      situacoes: {
        recebidas: EstatisticaCobranca;
        confirmadas: EstatisticaCobranca;
        aguardando: EstatisticaCobranca;
        vencidas: EstatisticaCobranca;
      };
      periodo: { dataInicio?: string; dataFim?: string };
    };

export interface UsuarioAdmin {
  id: string;
  nome: string;
  email: string;
  documento: string;
  tipoPessoa: TipoPessoa;
  telefone: string | null;
  role: Papel;
  ativo: boolean;
  emailVerificadoEm: string | null;
  createdAt: string;
  cidade: string;
  uf: string;
  senhaProvisoria: boolean;
  ultimoAcessoEm: string | null;
  carteira: { saldoCentavos: number; reservadoCentavos: number } | null;
  _count: { analisesSped: number; recargas: number; apiKeys: number };
  /** Buscas (lotes), XMLs entregues e tudo o que já saiu da carteira. */
  uso: { buscas: number; xmlsEntregues: number; consumidoCentavos: number };
}

export interface PaginaUsuarios {
  total: number;
  pagina: number;
  porPagina: number;
  itens: UsuarioAdmin[];
}

export interface FichaUsuario {
  user: UsuarioAdmin & {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string | null;
    bairro: string;
    asaasCustomerId: string | null;
  };
  lancamentos: Lancamento[];
  recargas: Array<{
    id: string;
    valorCentavos: number;
    status: StatusRecarga;
    asaasPaymentId: string | null;
    pagaEm: string | null;
    createdAt: string;
  }>;
  analises: Array<{
    id: string;
    nome: string;
    status: StatusAnalise;
    totalNotas: number;
    createdAt: string;
  }>;
  reservas: ReservaCredito[];
  apiKeys: Array<{
    id: string;
    nome: string;
    ultimoUso: string | null;
    expiraEm: string | null;
    revogadaEm: string | null;
    createdAt: string;
  }>;
  lotes: Job[];
  acessos: AcessoLog[];
  auditoria: LogAdmin[];
}

/** Tentativa de login — as que deram certo e as que não. */
export interface AcessoLog {
  id: string;
  userId: string | null;
  email: string;
  sucesso: boolean;
  motivo: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  user?: { id: string; nome: string; email: string } | null;
}

/** Propagação da página pública. Contagem anônima, sem cookie. */
export interface ResumoVisitas {
  periodoDias: number;
  visitas: number;
  visitantes: number;
  visitasHoje: number;
  visitantesHoje: number;
  cadastros: number;
  /** Visitantes que viraram conta, em % (0–100). */
  conversao: number;
  porDia: Array<{ dia: string; visitas: number; visitantes: number }>;
  origens: Array<{ origem: string; visitas: number }>;
  paginas: Array<{ caminho: string; visitas: number }>;
  funil: {
    total: EtapasFunil;
    porCanal: Array<EtapasFunil & { canal: string }>;
  };
  /** Visitantes únicos por tipo de aparelho. */
  dispositivos: Array<{ dispositivo: string; visitantes: number }>;
  /** Cliques nos botões marcados das páginas públicas. */
  cliques: Array<{ caminho: string; alvo: string; cliques: number }>;
}

/**
 * Visitantes do período e, das contas criadas nele, quantas chegaram a cada
 * etapa até hoje.
 */
export interface EtapasFunil {
  visitantes: number;
  cadastros: number;
  confirmados: number;
  recarregaram: number;
  usaram: number;
}

/** Uma ação administrativa registrada na trilha de auditoria. */
export interface LogAdmin {
  id: string;
  adminId: string;
  adminEmail: string;
  acao: string;
  alvoUserId: string | null;
  alvoEmail: string | null;
  detalhe: string | null;
  ip: string | null;
  createdAt: string;
}

/** API key vista do painel administrativo — nunca inclui o segredo. */
export interface ApiKeyAdmin {
  id: string;
  nome: string;
  ultimoUso: string | null;
  expiraEm: string | null;
  revogadaEm: string | null;
  createdAt: string;
  /** Derivado no servidor: expirada não é revogada, mas também não autentica. */
  expirada: boolean;
  user: { id: string; nome: string; email: string; ativo: boolean };
}

export interface PaginaApiKeys {
  total: number;
  pagina: number;
  porPagina: number;
  itens: ApiKeyAdmin[];
}

/** Solicitação de pagamento (recarga Pix) como o SISTEMA a registrou. */
export interface RecargaAdmin {
  id: string;
  valorCentavos: number;
  status: StatusRecarga;
  asaasPaymentId: string | null;
  expiraEm: string | null;
  pagaEm: string | null;
  createdAt: string;
  user: { id: string; nome: string; email: string };
}

export interface PaginaRecargas {
  total: number;
  pagina: number;
  porPagina: number;
  itens: RecargaAdmin[];
  /** Totais sobre TODAS as recargas, não só a página exibida. */
  resumo: Array<{
    status: StatusRecarga;
    valorCentavos: number;
    quantidade: number;
  }>;
}

export interface EventoWebhookAdmin {
  id: string;
  evento: string;
  paymentId: string | null;
  aplicado: boolean;
  motivo: string | null;
  recebidoEm: string;
}

// ── Suporte ──────────────────────────────────────────────────────────────────

export type StatusConversa = 'ABERTA' | 'FECHADA';

export interface MensagemSuporte {
  id: string;
  autor: 'CLIENTE' | 'SUPORTE';
  texto: string;
  createdAt: string;
}

/** Item da lista de conversas (GET /suporte/conversas). */
export interface ConversaResumo {
  id: string;
  assunto: string;
  status: StatusConversa;
  ultimaMensagemEm: string;
  createdAt: string;
  naoLidas: number;
}

/** Conversa aberta, com as mensagens (GET /suporte/conversas/:id). */
export interface Conversa extends Omit<ConversaResumo, 'naoLidas'> {
  mensagens: MensagemSuporte[];
}

/** Item da lista do admin (GET /admin/suporte). */
export interface ConversaAdminResumo extends ConversaResumo {
  user: { id: string; nome: string; email: string };
  aguardandoResposta: boolean;
  previa: string;
}

/** Conversa vista pelo admin, com o contexto do cliente. */
export interface ConversaAdmin extends Conversa {
  user: {
    id: string;
    nome: string;
    email: string;
    documento: string;
    telefone: string | null;
    createdAt: string;
    carteira: { saldoCentavos: number; reservadoCentavos: number } | null;
  };
}

// ── Captura automática via certificado A1 (src/dfe no backend) ──────────────

/** Até `ate` CNPJs ativos, `centavos` por mês para a conta inteira. */
export interface FaixaCaptura {
  ate: number;
  centavos: number;
}

/** GET /dfe/publico — sem login, para a página pública. */
export interface CapturaPublica {
  /** `false` enquanto a captura estiver em validação (só admin usa). */
  disponivel: boolean;
  faixas: FaixaCaptura[];
  /** Por CNPJ além da última faixa. */
  adicionalCentavos: number;
}

/** GET /dfe/config */
export interface ConfigCaptura {
  /** `false` = o servidor ainda não tem a chave que cifra os certificados. */
  habilitado: boolean;
  faixas: FaixaCaptura[];
  adicionalCentavos: number;
  carenciaDias: number;
}

/** GET /dfe/plano — o plano da conta (XMLs ilimitados). */
export interface PlanoCaptura {
  faixas: FaixaCaptura[];
  adicionalCentavos: number;
  carenciaDias: number;
  cnpjsAtivos: number;
  /** `null` = nenhum ciclo pago vigente. */
  ciclo: {
    inicio: string;
    fim: string;
    /** Teto da faixa paga: até aqui, ativar CNPJ não cobra nada. */
    cnpjsCobertos: number;
    valorCentavos: number;
  } | null;
  inadimplenteDesde: string | null;
  /** O que a próxima renovação cobra, com os CNPJs ativos de agora. */
  renovacaoCentavos: number;
  /** O que ativar mais um CNPJ cobra agora (0 = cabe na faixa paga). */
  custoProximoCnpjCentavos: number;
}

/** Certificado A1 cadastrado. O arquivo em si nunca volta da API. */
export interface CertificadoA1 {
  id: string;
  cnpj: string;
  titular: string;
  emissor: string;
  validoDe: string;
  validoAte: string;
  termoAceitoEm: string;
  createdAt: string;
  _count?: { empresas: number };
}

export type MotivoPausa = 'CLIENTE' | 'SALDO' | 'CERTIFICADO';

/** GET /dfe/empresas */
export interface EmpresaCaptura {
  id: string;
  cnpj: string;
  nome: string;
  ativa: boolean;
  motivoPausa: MotivoPausa | null;
  manifestarCiencia: boolean;
  ultimaConsultaEm: string | null;
  proximaConsultaEm: string;
  ultimoErro: string | null;
  certificado: CertificadoA1 | null;
  notas: {
    total: number;
    completas: number;
    aguardandoXml: number;
    manifestacaoComErro: number;
    foraDoPrazo: number;
  };
  createdAt: string;
}

export type SituacaoNota = 'AUTORIZADA' | 'CANCELADA' | 'DENEGADA';
export type ManifestacaoNota =
  'PENDENTE' | 'CIENCIA' | 'DISPENSADA' | 'FORA_DO_PRAZO' | 'ERRO';

export interface NotaRecebida {
  id: string;
  chave: string;
  emitenteDoc: string;
  emitenteNome: string;
  /** Decimal em texto ("1500.50"). */
  valor: string;
  dataEmissao: string;
  tipoNf: number;
  situacao: SituacaoNota;
  xmlCompleto: boolean;
  /** XML completo, pronto para download. */
  disponivel: boolean;
  manifestacao: ManifestacaoNota;
  manifestacaoErro: string | null;
  empresa: { cnpj: string; nome: string };
  recebidaEm: string;
}

/** GET /dfe/notas */
export interface PaginaNotasRecebidas {
  total: number;
  pagina: number;
  porPagina: number;
  valorTotal: string;
  itens: NotaRecebida[];
}

export interface EmpresaCapturaAdmin {
  id: string;
  cnpj: string;
  nome: string;
  cliente: { id: string; email: string; nome: string };
  ativa: boolean;
  motivoPausa: 'CLIENTE' | 'SALDO' | 'CERTIFICADO' | null;
  /** Do plano da conta do cliente. */
  inadimplenteDesde: string | null;
  planoVigente: boolean;
  ultimaConsultaEm: string | null;
  proximaConsultaEm: string;
  ultimoCStat: number | null;
  ultimoErro: string | null;
  /** Ativa com consulta vencida há mais de 20 min: laço possivelmente travado. */
  atrasada: boolean;
  certificadoValidoAte: string | null;
  notas: number;
  createdAt: string;
}

/** GET /admin/captura — panorama de operação da captura automática. */
export interface PanoramaCapturaAdmin {
  operacao: {
    habilitado: boolean;
    lacoLigado: boolean;
    ultimoTick: string | null;
    ambiente: 'producao' | 'homologacao';
    somenteAdmin: boolean;
    empresasAtrasadas: number;
  };
  config: {
    faixas: FaixaCaptura[];
    adicionalCentavos: number;
    carenciaDias: number;
  };
  planos: {
    vigentes: number;
    /** Soma do que os ciclos vigentes cobraram. */
    recorrenteCentavos: number;
    inadimplentes: number;
  };
  empresas: {
    total: number;
    ativas: number;
    pausadasCliente: number;
    pausadasSaldo: number;
    pausadasCertificado: number;
    comErro: number;
    clientes: number;
  };
  certificados: { total: number; vencendo30Dias: number; vencidos: number };
  notas: {
    total: number;
    completas: number;
    aguardandoXml: number;
    foraDoPrazo: number;
    comErroManifestacao: number;
    ultimas24h: number;
  };
  /** Planos debitados, em centavos. */
  receita: { mes: number; total: number };
  serieDiaria: Array<{ dia: string; total: number; completas: number }>;
  lista: EmpresaCapturaAdmin[];
}
